import React from 'react';
import PropTypes from 'prop-types';

import { useNode } from '@splashthat/splash-cms-engine';
import {
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { useSessions } from 'common/components/TouchpointEditor/contexts/SessionsContext';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';
import { SessionSearchContainer, SearchInput, StyledSearchIcon } from './SessionSearch.styles';

export default function SessionSearch({
    meta: { placeholder },
    styles: {
        backgroundColor,
        backgroundColorHover,
        fontColor,
        fontSize,
        fontFamily,
        iconColor,
        borderColor,
        borderColorHover,
        borderWidth,
        borderRadius,
    },
    selected,
    hovered,
}) {
    const node = useNode();
    const {
        theme: { activeFonts, colors },
    } = useTouchpointData();
    const sessions = useSessions();
    const customCssClassName = getCustomCssClassName(node);

    const onSearchTermChange = (event) => sessions.filters.setSearchTerm(event.target.value);

    return (
        <SessionSearchContainer
            onMouseMove={node.actions.onMouseMove()}
            onMouseLeave={node.actions.onMouseLeave()}
            onClick={node.actions.onClick()}
            className={customCssClassName}
        >
            <SearchInput
                type="text"
                value={sessions.filters.searchTerm}
                placeholder={placeholder}
                // Styles
                $fontFamily={activeFonts[fontFamily]}
                $fontColor={colors[fontColor]}
                $backgroundColor={colors[backgroundColor]}
                $backgroundColorHover={colors[backgroundColorHover]}
                $borderColor={colors[borderColor]}
                $borderColorHover={colors[borderColorHover]}
                $borderWidth={borderWidth}
                $borderRadius={borderRadius}
                $fontSize={fontSize}
                // Behaviors
                selected={selected}
                hovered={hovered}
                onChange={onSearchTermChange}
            />
            <StyledSearchIcon $iconColor={colors[iconColor]} aria-hidden="true" />
        </SessionSearchContainer>
    );
}

SessionSearch.splash = {
    related: {
        settings: React.lazy(() => import('./SessionSearch.tools')),
    },
    defaultProps: {
        meta: {
            placeholder: 'Search Sessions',
        },
        styles: {
            backgroundColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            backgroundColorHover: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            fontColorHover: BRAND_LIBRARY_DARK_TEXT_COLOR,
            iconColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            fontSize: 16,
            fontFamily: BRAND_LIBRARY_BODY_TEXT,
            borderColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            borderColorHover: BRAND_LIBRARY_DARK_TEXT_COLOR,
            borderWidth: {
                top: 1,
                bottom: 1,
                left: 1,
                right: 1,
            },
            borderRadius: 4,
        },
    },
};

SessionSearch.propTypes = {
    meta: PropTypes.shape({
        placeholder: PropTypes.string,
    }).isRequired,
    styles: PropTypes.shape({
        backgroundColor: PropTypes.number,
        backgroundColorHover: PropTypes.number,
        fontColor: PropTypes.number,
        fontColorHover: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
        borderColor: PropTypes.number,
        borderColorHover: PropTypes.number,
        borderStyle: PropTypes.string,
        borderWidth: PropTypes.object,
        borderRadius: PropTypes.number,
    }).isRequired,
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
};
